<template>
  <div class="NotificationsContainer">
    <div class="LoadingContainer" v-if="isLoadingSession">
      <loading-message :message="$t('notificationsModal.loadingMessage')" />
    </div>
    <div class="NotificationsModal" v-else>
      <h1 class="NotificationsModal-title">
        {{ $t('notificationsModal.title') }}
      </h1>
      <h2 class="NotificationsModal-subtitle">
        {{ $t('notificationsModal.subTitle') }}
      </h2>

      <div v-if="!mobileMode" class="seperator" />
      <div class="NotificationsModal-buttons">
        <large-button @click.native="onClose()">{{
          $t('notificationsModal.onCloseButton')
        }}</large-button>
        <large-button primary @click.native="handlePushNotification()">{{
          $t('notificationsModal.pushNotificationButton')
        }}</large-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { startSession } from '@/utils/session'
import LargeButton from '@/components/LargeButton.vue'
import PortalService from '@/services/PortalService'
import NetworkService from '@/services/NetworkService'
import LoadingMessage from '@/components/LoadingMessage.vue'
import setCookie from '@/utils/set-cookie'
import LoggerService from '@/services/LoggerService'

export default {
  components: { LargeButton, LoadingMessage },
  props: {
    modalData: { type: Object, required: true },
  },
  data() {
    return {
      selectedSubtopic: '',
      isLoadingSession: false,
    }
  },
  computed: {
    ...mapState({
      isMobileApp: state => state.app.isMobileApp,
    }),
    ...mapGetters({ mobileMode: 'app/mobileMode' }),
    title() {
      return this.modalData.topic
        ? `Choose a ${this.modalData.topic} subject`
        : 'Choose a subject'
    },
  },
  methods: {
    async handlePushNotification() {
      const { topic, selectedSubtopic } = this.modalData
      setCookie('hasSentPushTokenRegister', true)

      try {
        this.isLoadingSession = true

        const { token } = await PortalService.call('push.register')
        await NetworkService.savePushToken({ token })

        startSession(this.$router, topic, selectedSubtopic)
      } catch (error) {
        if (error.status !== 422) {
          LoggerService.noticeError(error)
        }

        startSession(this.$router, topic, selectedSubtopic)
      }
    },
    async onClose() {
      this.$emit('cancel')
      this.$store.dispatch('app/modal/hide')
      setCookie('hasSentPushTokenRegister', true)

      const { topic, selectedSubtopic } = this.modalData
      startSession(this.$router, topic, selectedSubtopic)
    },
  },
}
</script>

<style lang="scss" scoped>
h1,
h2,
p {
  margin: 0;
  padding: 0;
}

.NotificationsContainer {
  display: flex;
  height: 100%;

  // set a min-height for when loading message shows
  @include breakpoint-above('medium') {
    min-height: 200px;
  }
}

.NotificationsModal {
  @include flex-container(column);
  @include child-spacing(top, 24px);
  height: 100%;
}

.NotificationsModal-title {
  @include font-category('display-small');
  @include breakpoint-above('medium') {
    margin-top: 24px;
  }
}

.NotificationsModal-subtitle {
  @include font-category('heading');
  color: $c-secondary-grey;
}

.NotificationsModal-buttons {
  @include flex-container(column);
  @include child-spacing(top, 16px);
  margin-top: auto;
  margin-bottom: 4em;

  & > button {
    margin-bottom: 0.5em;
    padding: 1em 0;
  }

  @include breakpoint-above('medium') {
    @include child-spacing(top, 0);

    flex-direction: row;
    justify-content: space-around;
    margin-top: 1.4em;
    margin-bottom: 0;

    & > button {
      padding: 0.8em;
    }
  }
}

.LoadingContainer {
  @include font-category('display-small');
  margin: 0 auto;
  align-self: center;
}

.seperator {
  border: 1px solid $c-border-grey;
  width: 100%;
  height: 1px;
  margin-top: 2em;
}
</style>
